import {useState} from 'react'
import {Link, useNavigate, useParams, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {PerfilContratistaSection} from '@/components/perfil-contratista-section'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {DescargarEvidencia} from '@/components/descargar-evidencia'
import {LicenciaBadge} from '@/components/licencia-badge'
import {FichaEditDialog} from '@/components/ficha-edit-dialog'
import {EmployeeCumplimientoSection} from '@/components/employee-cumplimiento-section'
import {EmployeeDesignationsSection} from '@/components/employee-designations-section'
import {EmployeeEventosSection as EventosSection} from '@/components/employee-eventos-section'
import {EmployeeFichaSection as FichaSection} from '@/components/employee-ficha-section'
import {EmployeeHierarchySection as HierarchySection} from '@/components/employee-hierarchy-section'
import {EmployeeIncapacidadesSection} from '@/components/employee-incapacidades-section'
import {EmployeeExamenesSection} from '@/components/employee-examenes-section'
import {EnfermedadesLaboralesSection} from '@/components/employee-enfermedades-laborales-section'
import {EntrevistaTranscriptView} from '@/components/entrevista-transcript-view'
import {OwnerEntrevistaVersionRow} from '@/components/entrevista-version-row'
import {EmployeeIdentityCard} from '@/components/employee-identity-card'
import {SectionCard} from '@/components/section-card'
import {Fact, TagList} from '@/components/fact'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {rutaDeDescarga, useEvidence, useEmployeeEvidence} from '@/hooks/sgsst/evidencias/use-evidence'
import {useSgsst} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {useBusiness, useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useEmployee} from '@/hooks/empresa/identidad/use-employees'
import {
  useEmployeeEntrevista,
  useEmployeeEntrevistaHistorial,
  useReSolicitarEvaluacion,
} from '@/hooks/sgsst/riesgos/use-entrevista'
import {useEmployeeFicha} from '@/hooks/empresa/identidad/use-ficha'
import {perPerson} from '@/lib/sgsst-readiness'
import {
  isActionResponsibleRetentionConflict,
  isDesignationRetentionConflict,
  isEvidenceRetentionConflict,
  translateApiError,
} from '@/lib/errors'
import {api} from '@/lib/api'

type TabKey =
  | 'cumplimiento'
  | 'soportes'
  | 'designaciones'
  | 'examenes'
  | 'incapacidades'
  | 'eventos'
  | 'evaluacion'
  | 'ficha'
  | 'datos'
  | 'perfil-contratista'

/** Sin cargo en este organigrama: su perfil ocupacional lo elabora su empleador (E10-H09). */
const SIN_CARGO_PROPIO = ['contratista', 'temporal']

const TABS: {
  key: TabKey
  soloOperador?: boolean
  soloSinCargoPropio?: boolean
  /** Solo para la pestaña cuya etiqueta no vive bajo `employees.detail.tabs.*`. */
  etiqueta?: string
}[] = [
  {key: 'cumplimiento', soloOperador: true},
  // Segunda a propósito: para quien no opera el SG-SST es la primera visible y la que abre.
  {key: 'datos'},
  {key: 'soportes'},
  {key: 'designaciones', soloOperador: true},
  {key: 'examenes', soloOperador: true},
  {key: 'incapacidades'},
  {key: 'eventos', soloOperador: true},
  {key: 'evaluacion'},
  {key: 'ficha'},
  {
    key: 'perfil-contratista',
    soloOperador: true,
    soloSinCargoPropio: true,
    etiqueta: 'perfilContratista.tab',
  },
]

const TAB_KEYS: TabKey[] = TABS.map((pestana) => pestana.key)

function parseTab(value: string | null): TabKey | null {
  return TAB_KEYS.includes(value as TabKey) ? (value as TabKey) : null
}

export function EmployeeDetailPage() {
  const {t} = useTranslation()
  const {businessId, employeeId} = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const godReadOnly = useGodModeReadOnly()
  const [searchParams, setSearchParams] = useSearchParams()
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [retentionBlocked, setRetentionBlocked] = useState(false)
  const [designationBlocked, setDesignationBlocked] = useState(false)
  const [actionBlocked, setActionBlocked] = useState(false)
  const [confirmingResolicitar, setConfirmingResolicitar] = useState(false)
  const [resolicitarError, setResolicitarError] = useState<string | null>(null)

  const {data: employee, isLoading: employeeLoading} = useEmployee(businessId, employeeId)
  const {data: ficha, isLoading: fichaLoading, isError: fichaError} = useEmployeeFicha(businessId, employeeId)
  const {data: entrevista, isLoading: entrevistaLoading} = useEmployeeEntrevista(businessId, employeeId)
  const [historialPage, setHistorialPage] = useState(1)
  const {data: historial} = useEmployeeEntrevistaHistorial(businessId, employeeId, historialPage)
  const [soportesPage, setSoportesPage] = useState(1)
  const {data: soportes, isLoading: evidenceLoading} = useEmployeeEvidence(
    businessId!,
    employeeId ?? null,
    soportesPage
  )

  const {data: business} = useBusiness(businessId)
  const isOwner = business?.role === 'owner'
  // `useCapabilities` contesta la forma cerrada mientras la empresa carga: sin esperarla, la pestaña saltaba.
  const capabilitiesPendientes = business === undefined
  const capabilities = useCapabilities(businessId)
  const saludReservada = (deSalud: boolean) => deSalud && !capabilities.sgsst_custodiar_salud
  const canOperate = capabilities.sgsst_operar
  const canManagePeople = isOwner && !godReadOnly
  const canOperateEmployee = canOperate && !godReadOnly
  // Custodia de salud, no firma: sale de la capability y no de `business.role`, que llega elevado
  // a `owner` para las administradoras de plataforma que son empleadas del tenant.
  const canDecidirConcepto = capabilities.sgsst_custodiar_salud && !godReadOnly
  const canGrantAccess = canOperate && !godReadOnly
  const {data: sgsst} = useSgsst(businessId)
  const openAssessment = sgsst?.assessment && !sgsst.assessment.is_closed ? sgsst.assessment : undefined
  const {data: evidence} = useEvidence(openAssessment ? businessId : undefined)

  const reSolicitar = useReSolicitarEvaluacion(businessId!)

  const tab = parseTab(searchParams.get('tab')) ?? (canOperate ? 'cumplimiento' : 'datos')
  function setTab(next: TabKey) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.set('tab', next)
        return params
      },
      {replace: true}
    )
  }

  function goToStandard(year: number, standardCode: string) {
    navigate(`/businesses/${businessId}/sgsst?year=${year}#std-${standardCode}`)
  }

  function settleRosterChange() {
    queryClient.invalidateQueries({queryKey: ['employees', businessId]})
    queryClient.invalidateQueries({queryKey: ['sgsst', businessId]})
    navigate(`/businesses/${businessId}/employees`)
  }

  const deleteEmployee = useMutation({
    mutationFn: async () => api.delete(`/businesses/${businessId}/employees/${employeeId}`),
    onSuccess: settleRosterChange,
    onError: (err) => {
      if (isEvidenceRetentionConflict(err)) setRetentionBlocked(true)
      if (isDesignationRetentionConflict(err)) {
        setRetentionBlocked(true)
        setDesignationBlocked(true)
      }
      if (isActionResponsibleRetentionConflict(err)) {
        setRetentionBlocked(true)
        setActionBlocked(true)
      }
    },
  })

  const deactivateEmployee = useMutation({
    mutationFn: async () => api.patch(`/businesses/${businessId}/employees/${employeeId}`, {status: 'inactive'}),
    onSuccess: settleRosterChange,
  })

  if (employeeLoading || !employee || capabilitiesPendientes) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  const fullName = `${employee.name} ${employee.last_name}`
  const coverage =
    openAssessment && evidence && employee.status === 'active'
      ? perPerson(employee.id, openAssessment.items, evidence.items)
      : null

  return (
    <div className="space-y-5">
      <Link
        to={`/businesses/${businessId}/employees`}
        className="text-[13.5px] font-medium text-primary hover:underline">
        {t('employees.detail.back')}
      </Link>

      <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[20rem_minmax(0,1fr)]">
        <EmployeeIdentityCard
          businessId={businessId!}
          employee={employee}
          fullName={fullName}
          isOwner={isOwner}
          canOperate={canOperate}
          canOperateEmployee={canOperateEmployee}
          canGrantAccess={canGrantAccess}
          canManagePeople={canManagePeople}
          godReadOnly={godReadOnly}
          entrevistaCompleta={entrevista?.completed_at != null}
          cobertura={coverage}
          resolicitarError={resolicitarError}
          onResolicitar={() => {
            setResolicitarError(null)
            setConfirmingResolicitar(true)
          }}
          onDelete={() => setConfirmingDelete(true)}
        />

        <div className="min-w-0">
          <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
            <TabsList className="flex-wrap gap-x-4 gap-y-1">
              {TABS.filter(
                (pestana) =>
                  (canOperate || !pestana.soloOperador) &&
                  (!pestana.soloSinCargoPropio || SIN_CARGO_PROPIO.includes(employee.employment_type))
              ).map((pestana) => (
                <TabsTrigger key={pestana.key} value={pestana.key} data-testid={`tab-${pestana.key}`}>
                  {t(pestana.etiqueta ?? `employees.detail.tabs.${pestana.key}`)}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="datos" className="space-y-5 pt-4">
              <SectionCard title={t('employees.detail.datosLaboralesEyebrow')} bodyClassName="space-y-4 p-5">
                <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                  <Fact label={t('tags.kind.areas')}>
                    <TagList names={employee.areas.map((a) => a.name)} />
                  </Fact>
                  <Fact label={t('tags.kind.positions')}>
                    <TagList names={employee.positions.map((p) => p.name)} />
                  </Fact>
                  <Fact label={t('employees.fields.employmentType')}>
                    <Tag tone="neutral">{t(`employees.employmentType.${employee.employment_type}`)}</Tag>
                  </Fact>
                  <Fact label={t('employees.fields.contractType')}>
                    {employee.contract_type ? (
                      <Tag tone="neutral">{t(`employees.contractType.${employee.contract_type}`)}</Tag>
                    ) : (
                      <span className="text-muted-foreground">{t('employees.contractType.unspecified')}</span>
                    )}
                  </Fact>
                  {employee.licencia_estado && (
                    <Fact label={t('licencia.sectionTitle')}>
                      <span className="flex flex-wrap items-center gap-1.5">
                        {employee.licencia_categoria && (
                          <Tag tone="neutral">{t(`licencia.categorias.${employee.licencia_categoria}`)}</Tag>
                        )}
                        <LicenciaBadge
                          estado={employee.licencia_estado}
                          categoria={employee.licencia_categoria}
                          testId="employee-detail-licencia"
                        />
                      </span>
                    </Fact>
                  )}
                </div>
              </SectionCard>

              {canOperate && (
                <HierarchySection businessId={businessId!} employee={employee} canEdit={canOperate && !godReadOnly} />
              )}
            </TabsContent>

            <TabsContent value="ficha" className="space-y-5 pt-4">
              <SectionCard
                title={t('employees.detail.fichaEyebrow')}
                actions={
                  canOperate && !godReadOnly && ficha ? (
                    <FichaEditDialog
                      businessId={businessId!}
                      employeeId={employee.id}
                      employeeName={fullName}
                      ficha={ficha}
                    />
                  ) : undefined
                }>
                <FichaSection ficha={ficha} isLoading={fichaLoading} isError={fichaError} />
              </SectionCard>
            </TabsContent>

            <TabsContent value="evaluacion" className="space-y-5 pt-4">
              <SectionCard
                title={
                  <span className="inline-flex flex-wrap items-center gap-2">
                    {t('employees.detail.evaluacionEyebrow')}
                    {entrevista?.modo === 'reconfirmacion' && (
                      <Tag tone="amber">{t('employees.detail.evaluacionHistorialModoReconfirmacion')}</Tag>
                    )}
                  </span>
                }
                bodyClassName="space-y-4 p-5">
                <EntrevistaTranscriptView
                  businessId={businessId!}
                  employeeId={employeeId!}
                  entrevista={entrevista}
                  isLoading={entrevistaLoading}
                />

                <details className="surface-divider border-t pt-4">
                  <summary className="cursor-pointer text-sm font-medium text-primary" data-testid="historial-toggle">
                    {t('employees.detail.evaluacionHistorialTitle')}
                  </summary>
                  <div className="pt-3">
                    {!historial || historial.total <= 1 ? (
                      <p className="text-sm text-muted-foreground" data-testid="historial-empty">
                        {t('employees.detail.evaluacionHistorialEmpty')}
                      </p>
                    ) : (
                      <>
                        <div data-testid="historial-list">
                          {historial.items
                            .filter((v) => !v.vigente)
                            .map((version) => (
                              <OwnerEntrevistaVersionRow
                                key={version.id}
                                businessId={businessId!}
                                employeeId={employeeId!}
                                version={version}
                              />
                            ))}
                        </div>
                        {/* `total - 1`: la versión vigente viaja en la página pero se pinta arriba, no acá. */}
                        {historial.total - 1 > historial.page_size && (
                          <PaginationBar
                            page={historial.page}
                            pageSize={historial.page_size}
                            total={historial.total - 1}
                            onPageChange={setHistorialPage}
                          />
                        )}
                      </>
                    )}
                  </div>
                </details>
              </SectionCard>
            </TabsContent>

            <TabsContent value="soportes" className="space-y-5 pt-4">
              <SectionCard title={t('employees.detail.soportesEyebrow')} bodyClassName="space-y-4 p-5">
                {evidenceLoading ? (
                  <Skeleton className="h-24 w-full" />
                ) : !soportes?.total ? (
                  <p className="text-sm text-muted-foreground">{t('evidence.employeeDialogEmpty')}</p>
                ) : (
                  <div className="space-y-4">
                    {soportes.items.map((year) => (
                      <section key={year.year}>
                        <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                          {year.year}
                          {year.is_closed && ` · ${t('sgsst.cycle.closed')}`}
                        </p>
                        <ul className="mt-1">
                          {year.items.map((item) => (
                            <li
                              key={item.id}
                              className="surface-divider flex items-baseline gap-3 border-b py-2 text-sm last:border-b-0">
                              <button
                                type="button"
                                onClick={() => goToStandard(year.year, item.standard_code)}
                                title={t('evidence.viewInAssessment')}
                                className="w-12 shrink-0 text-left font-mono text-xs font-medium text-primary hover:underline">
                                {item.standard_code}
                              </button>
                              <span
                                className="min-w-0 flex-1 truncate font-medium text-foreground"
                                title={item.filename}>
                                {item.filename}
                              </span>
                              {item.pending_confirmation && <Tag tone="amber">{t('evidence.pendingConfirmation')}</Tag>}
                              <DescargarEvidencia
                                ruta={rutaDeDescarga(businessId!, item.id)}
                                reservado={saludReservada(item.de_salud)}
                                className="shrink-0 text-xs font-semibold text-primary hover:underline">
                                {!saludReservada(item.de_salud) && (
                                  <>
                                    <span aria-hidden="true">↓ </span>
                                    {t('evidence.download')}
                                  </>
                                )}
                              </DescargarEvidencia>
                            </li>
                          ))}
                        </ul>
                      </section>
                    ))}
                    <PaginationBar
                      page={soportes.page}
                      pageSize={soportes.page_size}
                      total={soportes.total}
                      onPageChange={setSoportesPage}
                    />
                  </div>
                )}
                {!evidenceLoading && !!coverage?.gaps.length && openAssessment && (
                  <section data-testid="employee-gaps" className="surface-divider border-t pt-4">
                    <p className="tone-amber inline-block rounded px-1.5 py-0.5 font-mono text-[10px] font-medium tracking-wider uppercase">
                      {t('evidence.gapsTitle')}
                    </p>
                    <ul className="mt-1">
                      {coverage.gaps.map((item) => (
                        <li
                          key={item.code}
                          className="surface-divider flex items-baseline gap-3 border-b py-2 text-sm last:border-b-0">
                          <button
                            type="button"
                            onClick={() => goToStandard(openAssessment.period_year, item.code)}
                            title={t('evidence.viewInAssessment')}
                            className="w-12 shrink-0 text-left font-mono text-xs font-medium text-primary hover:underline">
                            {item.code}
                          </button>
                          <span className="min-w-0 flex-1 truncate text-foreground" title={item.name}>
                            {item.name}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </SectionCard>
            </TabsContent>

            {canOperate && (
              <TabsContent value="designaciones" className="space-y-5 pt-4">
                <SectionCard title={t('employees.detail.designacionesEyebrow')} bodyClassName="space-y-4 p-5">
                  <EmployeeDesignationsSection businessId={businessId!} employeeId={employee.id} />
                </SectionCard>
              </TabsContent>
            )}

            {canOperate && (
              <TabsContent value="cumplimiento" className="space-y-5 pt-4">
                {/* Dos puertas: cuatro actos son `require_firmante` y «Pedirlo» es `require_operador`;
                  el cinturón de modo dios no reemplaza a `sgsst_firmar`. */}
                <EmployeeCumplimientoSection
                  businessId={businessId!}
                  employeeId={employeeId!}
                  puedeOperar={canOperateEmployee}
                  puedeFirmar={capabilities.sgsst_firmar && !godReadOnly}
                  puedeCustodiar={canDecidirConcepto}
                  razonBloqueo={godReadOnly ? t('perfilCargo.bloqueoModoDios') : capabilities.firmar_bloqueado_por}
                />
              </TabsContent>
            )}

            <TabsContent value="incapacidades" className="space-y-5 pt-4">
              <SectionCard title={t('employees.detail.incapacidadesEyebrow')}>
                <EmployeeIncapacidadesSection
                  businessId={businessId!}
                  employeeId={employee.id}
                  canOperate={canOperateEmployee}
                />
              </SectionCard>
              {canOperate && (
                <SectionCard title={t('employees.detail.elEyebrow')}>
                  <EnfermedadesLaboralesSection
                    businessId={businessId!}
                    employeeId={employee.id}
                    employeeName={fullName}
                    canEdit={canOperateEmployee}
                  />
                </SectionCard>
              )}
            </TabsContent>
            {canOperate && (
              <TabsContent value="eventos" className="space-y-5 pt-4">
                <SectionCard title={t('employees.detail.eventosEyebrow')}>
                  <EventosSection businessId={businessId!} employeeId={employee.id} employeeName={fullName} />
                </SectionCard>
              </TabsContent>
            )}
            {canOperate && (
              <TabsContent value="examenes" className="space-y-5 pt-4">
                <SectionCard title={t('employees.detail.examenesEyebrow')}>
                  <EmployeeExamenesSection
                    businessId={businessId!}
                    employeeId={employee.id}
                    employeeName={fullName}
                    canOperate={canOperateEmployee}
                    canDecidir={canDecidirConcepto}
                    decisionBloqueadaPor={
                      canDecidirConcepto
                        ? null
                        : godReadOnly
                          ? t('examenes.normaAnterior.bloqueoModoDios')
                          : t('examenes.normaAnterior.soloCustodio')
                    }
                  />
                </SectionCard>
              </TabsContent>
            )}
            {canOperate && SIN_CARGO_PROPIO.includes(employee.employment_type) && (
              <TabsContent value="perfil-contratista" className="space-y-5 pt-4">
                <SectionCard title={t('perfilContratista.titulo')}>
                  <PerfilContratistaSection
                    businessId={businessId!}
                    employeeId={employee.id}
                    canOperate={canOperateEmployee}
                    canFirmar={capabilities.sgsst_firmar && !godReadOnly}
                    razonBloqueoFirma={
                      capabilities.sgsst_firmar && !godReadOnly ? null : t('perfilContratista.soloFirmante')
                    }
                  />
                </SectionCard>
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>

      <ConfirmDialog
        open={confirmingDelete && !retentionBlocked}
        onOpenChange={(open) => !open && setConfirmingDelete(false)}
        title={t('employees.deleteDialogTitle', {name: fullName})}
        description={t('employees.deleteDialogDescription', {name: fullName})}
        confirmLabel={t('employees.deleteConfirmAction')}
        cancelLabel={t('common.cancel')}
        pending={deleteEmployee.isPending}
        onConfirm={() => deleteEmployee.mutate()}
        porque={{
          label: t('employees.deleteDesactivarLabel'),
          contenido: t('employees.deleteDesactivarPorque'),
          testId: 'employee-delete-desactivar-porque',
          contenidoTestId: 'employee-delete-desactivar-note',
        }}
      />
      <ConfirmDialog
        open={confirmingDelete && retentionBlocked}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmingDelete(false)
            setRetentionBlocked(false)
            setDesignationBlocked(false)
            setActionBlocked(false)
          }
        }}
        title={t('evidence.retention.title', {name: fullName})}
        description={
          actionBlocked
            ? t('plan.retention.body')
            : designationBlocked
              ? t('designations.retention.body')
              : t('evidence.retention.body')
        }
        porque={{
          label: t('evidence.retention.porqueLabel'),
          contenido: (
            <p>
              {actionBlocked
                ? t('plan.retention.porqueBody')
                : designationBlocked
                  ? t('designations.retention.porqueBody')
                  : t('evidence.retention.porqueBody')}
            </p>
          ),
          testId: 'retention-porque',
          contenidoTestId: 'retention-porque-contenido',
        }}
        confirmLabel={t('evidence.retention.deactivate')}
        cancelLabel={t('common.cancel')}
        pending={deactivateEmployee.isPending}
        onConfirm={() => deactivateEmployee.mutate()}
      />
      <ConfirmDialog
        open={confirmingResolicitar}
        onOpenChange={setConfirmingResolicitar}
        title={t('ipevr.resolicitar.confirmTitleUno', {name: fullName})}
        description={t('ipevr.resolicitar.confirmBodyUno', {name: fullName})}
        confirmLabel={t('ipevr.resolicitar.confirmActionUno')}
        cancelLabel={t('common.cancel')}
        pending={reSolicitar.isPending}
        onConfirm={() =>
          reSolicitar.mutate(employee.id, {
            onSuccess: () => setConfirmingResolicitar(false),
            onError: (err) => {
              setConfirmingResolicitar(false)
              setResolicitarError(translateApiError(err))
            },
          })
        }
      />
    </div>
  )
}
